/**
 * Browser JavaScript cannot reliably block OS Alt+Tab, screenshots, other devices,
 * or fully disable developer tools. See LIMITATIONS.md.
 */

export const CLOSED_BOOK_FEATURE_CODE = 'closed_book_security';

export const SECURITY_EVENTS = {
  EXAM_SECURITY_STARTED: 'EXAM_SECURITY_STARTED',
  EXAM_SESSION_RESTORED: 'EXAM_SESSION_RESTORED',
  PAGE_RELOAD_DETECTED: 'PAGE_RELOAD_DETECTED',

  FULLSCREEN_ENTERED: 'FULLSCREEN_ENTERED',
  FULLSCREEN_EXITED: 'FULLSCREEN_EXITED',
  FULLSCREEN_REENTRY_FAILED: 'FULLSCREEN_REENTRY_FAILED',

  TAB_HIDDEN: 'TAB_HIDDEN',
  TAB_VISIBLE: 'TAB_VISIBLE',

  WINDOW_BLUR: 'WINDOW_BLUR',
  WINDOW_FOCUS: 'WINDOW_FOCUS',

  COPY_ATTEMPT: 'COPY_ATTEMPT',
  PASTE_ATTEMPT: 'PASTE_ATTEMPT',
  CUT_ATTEMPT: 'CUT_ATTEMPT',
  CONTEXT_MENU_ATTEMPT: 'CONTEXT_MENU_ATTEMPT',

  PRINT_ATTEMPT: 'PRINT_ATTEMPT',
  BLOCKED_SHORTCUT: 'BLOCKED_SHORTCUT',

  NAVIGATION_ATTEMPT: 'NAVIGATION_ATTEMPT',
  EXTERNAL_LINK_ATTEMPT: 'EXTERNAL_LINK_ATTEMPT',

  SCREEN_SHARE_STARTED: 'SCREEN_SHARE_STARTED',
  SCREEN_SHARE_STOPPED: 'SCREEN_SHARE_STOPPED',
  SCREEN_SHARE_DENIED: 'SCREEN_SHARE_DENIED',
  SCREEN_SHARE_ERROR: 'SCREEN_SHARE_ERROR',

  POLICY_VIOLATION: 'POLICY_VIOLATION'
};

export const EVENT_SEVERITY = {
  [SECURITY_EVENTS.EXAM_SECURITY_STARTED]: 'info',
  [SECURITY_EVENTS.EXAM_SESSION_RESTORED]: 'info',
  [SECURITY_EVENTS.PAGE_RELOAD_DETECTED]: 'medium',
  [SECURITY_EVENTS.FULLSCREEN_ENTERED]: 'info',
  [SECURITY_EVENTS.FULLSCREEN_EXITED]: 'high',
  [SECURITY_EVENTS.FULLSCREEN_REENTRY_FAILED]: 'high',
  [SECURITY_EVENTS.TAB_HIDDEN]: 'high',
  [SECURITY_EVENTS.TAB_VISIBLE]: 'info',
  [SECURITY_EVENTS.WINDOW_BLUR]: 'medium',
  [SECURITY_EVENTS.WINDOW_FOCUS]: 'info',
  [SECURITY_EVENTS.COPY_ATTEMPT]: 'low',
  [SECURITY_EVENTS.PASTE_ATTEMPT]: 'medium',
  [SECURITY_EVENTS.CUT_ATTEMPT]: 'low',
  [SECURITY_EVENTS.CONTEXT_MENU_ATTEMPT]: 'low',
  [SECURITY_EVENTS.PRINT_ATTEMPT]: 'high',
  [SECURITY_EVENTS.BLOCKED_SHORTCUT]: 'medium',
  [SECURITY_EVENTS.NAVIGATION_ATTEMPT]: 'high',
  [SECURITY_EVENTS.EXTERNAL_LINK_ATTEMPT]: 'high',
  [SECURITY_EVENTS.SCREEN_SHARE_STARTED]: 'info',
  [SECURITY_EVENTS.SCREEN_SHARE_STOPPED]: 'critical',
  [SECURITY_EVENTS.SCREEN_SHARE_DENIED]: 'critical',
  [SECURITY_EVENTS.SCREEN_SHARE_ERROR]: 'high',
  [SECURITY_EVENTS.POLICY_VIOLATION]: 'high'
};

export const TELEMETRY_EVENTS = new Set([
  SECURITY_EVENTS.WINDOW_FOCUS,
  SECURITY_EVENTS.TAB_VISIBLE,
  SECURITY_EVENTS.FULLSCREEN_ENTERED
]);

export const CRITICAL_EVENTS = new Set([
  SECURITY_EVENTS.SCREEN_SHARE_STOPPED,
  SECURITY_EVENTS.SCREEN_SHARE_DENIED,
  SECURITY_EVENTS.FULLSCREEN_EXITED,
  SECURITY_EVENTS.NAVIGATION_ATTEMPT,
  SECURITY_EVENTS.EXTERNAL_LINK_ATTEMPT,
  SECURITY_EVENTS.PRINT_ATTEMPT
]);

export const SECURITY_STATES = {
  INITIALIZING: 'INITIALIZING',
  CHECKING_REQUIREMENTS: 'CHECKING_REQUIREMENTS',
  WAITING_FOR_FULLSCREEN: 'WAITING_FOR_FULLSCREEN',
  WAITING_FOR_SCREEN_SHARE: 'WAITING_FOR_SCREEN_SHARE',
  SECURED: 'SECURED',
  EXAM_ACTIVE: 'EXAM_ACTIVE',
  SECURITY_INTERRUPTION: 'SECURITY_INTERRUPTION',
  REMEDIATION_REQUIRED: 'REMEDIATION_REQUIRED',
  SUBMITTED: 'SUBMITTED',
  TERMINATED: 'TERMINATED',
  ABANDONED: 'ABANDONED'
};

export const CANDIDATE_SECURITY_STATUS = {
  SECURE: 'SECURE',
  WARNING: 'WARNING',
  NON_COMPLIANT: 'NON_COMPLIANT',
  DISCONNECTED: 'DISCONNECTED'
};

export const RISK_WEIGHTS = {
  [SECURITY_EVENTS.FULLSCREEN_EXITED]: 10,
  [SECURITY_EVENTS.TAB_HIDDEN]: 10,
  [SECURITY_EVENTS.WINDOW_BLUR]: 5,
  [SECURITY_EVENTS.PRINT_ATTEMPT]: 15,
  [SECURITY_EVENTS.NAVIGATION_ATTEMPT]: 15,
  [SECURITY_EVENTS.EXTERNAL_LINK_ATTEMPT]: 15,
  [SECURITY_EVENTS.SCREEN_SHARE_STOPPED]: 30,
  [SECURITY_EVENTS.SCREEN_SHARE_DENIED]: 30,
  [SECURITY_EVENTS.PASTE_ATTEMPT]: 5,
  [SECURITY_EVENTS.COPY_ATTEMPT]: 3,
  [SECURITY_EVENTS.BLOCKED_SHORTCUT]: 3
};

export const EVENT_DEDUP_WINDOW_MS = 500;
export const METADATA_MAX_BYTES = 16 * 1024;
export const MESSAGE_MAX_CHARS = 500;

export function clampRiskScore(score) {
  const n = Number(score) || 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}
